import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { LICENSE_TIERS, type LicenseTier, type TeamBalance } from '@/api-models/balance';
import { updateTeamLicense } from '@/api-models/admin/credits';
import { formatUsd } from '@/api-models/usage';
import { formatDate } from '@/features/admin/format';
import { Button } from '@/ui/button/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/Card';
import { Input } from '@/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';

/** RFC 3339 → the `yyyy-mm-dd` a date input wants. */
function toDateInput(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : '';
}

/**
 * The licence a space holds: tier, billed seats, and the date its periods are
 * anchored to.
 *
 * The anchor is editable because it decides where every period boundary falls — a
 * licence sold on the 28th renews on the 28th — and an operator who typed the wrong
 * start date would otherwise be stuck with skewed renewals for the life of the
 * account. Changing the *tier* deliberately leaves the anchor alone, so an upgrade
 * cannot mint a fresh allowance by restarting the period.
 */
export function LicenseCard({
  teamId,
  balance,
  onSaved,
}: {
  teamId: string;
  balance: TeamBalance;
  onSaved: () => Promise<unknown>;
}) {
  const [tier, setTier] = useState<LicenseTier>(balance.license_tier);
  const [seats, setSeats] = useState(balance.seats_are_overridden ? String(balance.seats) : '');
  const [startedAt, setStartedAt] = useState(toDateInput(balance.license_started_at));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTier(balance.license_tier);
    setSeats(balance.seats_are_overridden ? String(balance.seats) : '');
    setStartedAt(toDateInput(balance.license_started_at));
  }, [balance.license_tier, balance.seats, balance.seats_are_overridden, balance.license_started_at]);

  const seatsTrimmed = seats.trim();
  const parsedSeats = seatsTrimmed === '' ? null : Number(seatsTrimmed);
  const seatsValid = parsedSeats === null || (Number.isInteger(parsedSeats) && parsedSeats >= 0);

  // Mirror the backend formula so the operator sees the figure before saving.
  const spec = LICENSE_TIERS.find((t) => t.value === tier);
  const effectiveSeats = parsedSeats ?? balance.seats;
  const previewMicroUsd =
    tier === 'free'
      ? 5_000_000
      : Math.floor(((spec?.pricePerSeat ?? 0) * 1_000_000 * (100 - balance.margin_percent)) / 100) * effectiveSeats;

  async function save() {
    if (!seatsValid) return;
    setIsSaving(true);
    try {
      await updateTeamLicense(teamId, {
        license_tier: tier,
        seats: parsedSeats,
        license_started_at: startedAt ? new Date(`${startedAt}T00:00:00Z`).toISOString() : null,
      });
      await onSaved();
      toast.success('Licence updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the licence');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card padding="none" radius="2xl" className="overflow-hidden">
      <CardHeader>
        <CardTitle>Licence</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-2xs font-medium text-fg-secondary">Tier</span>
            <Select value={tier} onValueChange={(v) => setTier(v as LicenseTier)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LICENSE_TIERS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                    {t.pricePerSeat > 0 ? ` — $${t.pricePerSeat}/seat/mo` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-2xs font-medium text-fg-secondary">Billed seats</span>
            <Input
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              placeholder={`Active members (${balance.seats})`}
              inputMode="numeric"
            />
            <span className="text-2xs text-fg-muted">Blank counts active members.</span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-2xs font-medium text-fg-secondary">Licence start</span>
            <Input type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
            <span className="text-2xs text-fg-muted">Anchors every renewal date.</span>
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-surface-3 px-3.5 py-2.5">
          <div className="text-xs text-fg-secondary">
            Allowance <span className="font-medium text-fg tabular-nums">{formatUsd(previewMicroUsd)}</span> per period
            {tier !== 'free' && (
              <span className="text-fg-muted">
                {' '}
                · {effectiveSeats} seat{effectiveSeats === 1 ? '' : 's'} at {balance.margin_percent}% margin
              </span>
            )}
          </div>
          <div className="text-2xs text-fg-muted tabular-nums">
            Current period {formatDate(balance.period_start)} → {formatDate(balance.period_end)}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={isSaving || !seatsValid}>
            {isSaving ? 'Saving…' : 'Save licence'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
